import React from 'react';
import { Badge } from '@usepepper/ui/atoms/badge';
import { createStyles } from '@/contexts/theme-context';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { OrderEntryLabel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { Spacing } from '@usepepper/sprout-kit';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useOrderEntryLabels } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-labels';
import { View } from 'react-native';

interface OrderEntryTableItemNameCellProps {
  imageUrl?: string;
  labels?: OrderEntryLabel;
  name: string;
  orderGuideLabels: { level: string; orderGuideName: string }[];
}

export const OrderEntryTableItemNameCell: React.FC<
  OrderEntryTableItemNameCellProps
> = ({ imageUrl, labels, name, orderGuideLabels }) => {
  const [styles] = useStyles();
  const badges = useOrderEntryLabels(labels, orderGuideLabels);

  return (
    <View style={styles.container}>
      <ItemImage
        alt={name}
        cachePolicy="memory-disk"
        imageURLs={imageUrl ? [imageUrl] : []}
        size={Spacing.SPACE_XLARGE}
        style={styles.itemImage}
      />
      <View collapsable={false} style={styles.labelContainer}>
        <Typography numberOfLines={2} variant="paragraph-md-600">
          {name}
        </Typography>
        {badges.length > 0 ? (
          <View style={styles.badgesContainer}>
            {badges.map((badge) => (
              <View key={badge.key} style={styles.badgeWrapper}>
                <Badge
                  label={badge.label}
                  leftElement={badge.leftElement}
                  size={badge.size}
                  style={styles.badge}
                  textStyle={styles.badgeText}
                  variant={badge.variant}
                />
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  badge: {
    width: '100%',
  },
  badgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceXsmall,
  },
  badgeText: {
    flexShrink: 1,
  },
  badgeWrapper: {
    alignSelf: 'flex-start',
    flexShrink: 1,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  container: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  itemImage: {
    alignSelf: 'flex-start',
  },
  labelContainer: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
    minWidth: 0,
  },
}));
